import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  FilterBar,
  FilterSelect,
  Input,
  Modal,
  Select,
  TableSkeleton,
  TableWrap,
  Td,
  Textarea,
  Th,
  Tr
} from '../../components/admin/ui';
import { useConfirm, useToast } from '../../components/admin/overlays';
import { IconDownload, IconPlus, IconRefresh } from '../../components/admin/icons';
import { formatDateTime, formatNumber } from '../../components/admin/labels';

/**
 * Acquisition tracking: where customer reports come from, and what each one
 * cost for campaigns with spend entered.
 *
 * Every figure uses the same rules as the server report — UK calendar days,
 * latest-touch attribution, customer-submitted cases only. Anything that
 * can't be calculated (no spend entered, nothing to divide by) shows as
 * "Unavailable", never as zero.
 */

const ukDate = (offsetDays = 0) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(
    new Date(Date.now() - offsetDays * 24 * 60 * 60 * 1000)
  );

const UNAVAILABLE = <span className="text-ink-400">Unavailable</span>;

const money = (value, currency) =>
  value == null
    ? UNAVAILABLE
    : new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'USD' }).format(value);

const percent = (value) => (value == null ? UNAVAILABLE : `${(value * 100).toFixed(1)}%`);

const COUNT_KEYS = ['visits', 'starts', 'submissions', 'valid', 'duplicate', 'invalid', 'pending'];

const sumRows = (rows) => {
  const totals = Object.fromEntries(COUNT_KEYS.map((k) => [k, rows.reduce((n, r) => n + r[k], 0)]));
  totals.startToSubmitRate = totals.starts > 0 ? totals.submissions / totals.starts : null;
  return totals;
};

const daysBetween = (start, end) =>
  Math.round((new Date(`${end}T00:00:00Z`) - new Date(`${start}T00:00:00Z`)) / 86400000) + 1;

const EMPTY_CAMPAIGN = {
  name: 'Reddit validation test',
  source: 'reddit',
  utmCampaign: '',
  currency: 'USD',
  budget: '150',
  startDate: ukDate(),
  endDate: ukDate(-13),
  spend: '',
  notes: ''
};

const Metric = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</dt>
    <dd className="mt-1 font-display text-lg font-700 tabular-nums text-ink-900">{children}</dd>
  </div>
);

const CampaignCard = ({ campaign, onEdit, onDelete }) => {
  const m = campaign.metrics;
  return (
    <Card>
      <CardHeader
        title={campaign.name}
        description={`utm_source=${campaign.source} · utm_campaign=${campaign.utmCampaign} · ${campaign.startDate} → ${campaign.endDate} (${daysBetween(campaign.startDate, campaign.endDate)} days, UK time)`}
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => onEdit(campaign)}>
              Edit / enter spend
            </Button>
            <Button variant="dangerGhost" size="sm" onClick={() => onDelete(campaign)}>
              Delete
            </Button>
          </>
        }
      />
      <CardBody>
        <dl className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
          <Metric label="Budget cap">{money(campaign.budget, campaign.currency)}</Metric>
          <Metric label="Spend">{money(campaign.spend, campaign.currency)}</Metric>
          <Metric label="Landing visits">{m ? formatNumber(m.visits) : UNAVAILABLE}</Metric>
          <Metric label="Report starts">{m ? formatNumber(m.starts) : UNAVAILABLE}</Metric>
          <Metric label="Completed reports">{m ? formatNumber(m.submissions) : UNAVAILABLE}</Metric>
          <Metric label="Valid cases">{m ? formatNumber(m.valid) : UNAVAILABLE}</Metric>
          <Metric label="Start → submit">{percent(m?.startToSubmitRate)}</Metric>
          <Metric label="Cost / completed report">{money(campaign.costPerSubmission, campaign.currency)}</Metric>
          <Metric label="Cost / valid case">{money(campaign.costPerValidCase, campaign.currency)}</Metric>
          <Metric label="Awaiting review">{m ? formatNumber(m.pending) : UNAVAILABLE}</Metric>
        </dl>
        <p className="mt-4 text-xs text-ink-500">
          {campaign.spendUpdatedAt
            ? `Spend last entered ${formatDateTime(campaign.spendUpdatedAt)}.`
            : 'No spend entered yet — cost figures stay unavailable until it is.'}
          {m ? ` ${m.duplicate} duplicate and ${m.invalid} invalid report(s) excluded from valid cases.` : ''}
        </p>
      </CardBody>
    </Card>
  );
};

const CampaignModal = ({ campaign, onClose, onSaved }) => {
  const toast = useToast();
  const [form, setForm] = useState(() =>
    campaign._id
      ? {
          ...campaign,
          budget: campaign.budget ?? '',
          spend: campaign.spend ?? '',
          notes: campaign.notes || ''
        }
      : EMPTY_CAMPAIGN
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const save = async () => {
    setSaving(true);
    setErrors({});
    const payload = {
      name: form.name,
      source: form.source,
      utmCampaign: form.utmCampaign,
      currency: form.currency,
      budget: form.budget === '' ? null : Number(form.budget),
      startDate: form.startDate,
      endDate: form.endDate,
      spend: form.spend === '' ? null : Number(form.spend),
      notes: form.notes
    };
    try {
      const { data } = campaign._id
        ? await api.patch(`/api/admin/acquisition/campaigns/${campaign._id}`, payload)
        : await api.post('/api/admin/acquisition/campaigns', payload);
      onSaved(data);
    } catch (err) {
      const list = err.response?.data?.errors;
      if (list) setErrors(Object.fromEntries(list.map((e) => [e.path || e.param, e.msg])));
      else toast(err.response?.data?.error || 'Could not save the campaign.', { tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={campaign._id ? 'Edit campaign' : 'New campaign'}
      description="Matched to reports by utm_source and utm_campaign, within these dates."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} loading={saving}>
            Save campaign
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Name" htmlFor="c-name" required error={errors.name} className="sm:col-span-2">
          <Input id="c-name" value={form.name} onChange={set('name')} />
        </Field>
        <Field label="utm_source" htmlFor="c-source" required error={errors.source} hint="e.g. reddit">
          <Input id="c-source" value={form.source} onChange={set('source')} />
        </Field>
        <Field label="utm_campaign" htmlFor="c-campaign" required error={errors.utmCampaign} hint="Exactly as in the ad URL">
          <Input id="c-campaign" value={form.utmCampaign} onChange={set('utmCampaign')} />
        </Field>
        <Field label="Start date (UK)" htmlFor="c-start" required error={errors.startDate}>
          <Input id="c-start" type="date" value={form.startDate} onChange={set('startDate')} />
        </Field>
        <Field label="End date (UK)" htmlFor="c-end" required error={errors.endDate}>
          <Input id="c-end" type="date" value={form.endDate} onChange={set('endDate')} />
        </Field>
        <Field label="Currency" htmlFor="c-currency">
          <Select id="c-currency" value={form.currency} onChange={set('currency')}>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
            <option value="EUR">EUR</option>
          </Select>
        </Field>
        <Field label="Budget cap" htmlFor="c-budget" error={errors.budget}>
          <Input id="c-budget" type="number" min="0" step="0.01" value={form.budget} onChange={set('budget')} />
        </Field>
        <Field
          label="Spend to date"
          htmlFor="c-spend"
          error={errors.spend}
          hint="From Reddit Ads Manager for the same dates. Leave blank if unknown."
          className="sm:col-span-2"
        >
          <Input id="c-spend" type="number" min="0" step="0.01" value={form.spend} onChange={set('spend')} />
        </Field>
        <Field label="Notes" htmlFor="c-notes" className="sm:col-span-2">
          <Textarea id="c-notes" rows={2} value={form.notes} onChange={set('notes')} />
        </Field>
      </div>
    </Modal>
  );
};

const toCsv = (rows) => {
  const header = ['Source', 'Campaign', 'Visits', 'Report starts', 'Completed reports', 'Valid', 'Duplicate', 'Invalid', 'Pending', 'Start to submit %'];
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = rows.map((r) =>
    [r.source, r.campaign, r.visits, r.starts, r.submissions, r.valid, r.duplicate, r.invalid, r.pending,
      r.startToSubmitRate == null ? '' : (r.startToSubmitRate * 100).toFixed(1)].map(escape).join(',')
  );
  return [header.map(escape).join(','), ...lines].join('\n');
};

const AcquisitionTracking = () => {
  const toast = useToast();
  const confirm = useConfirm();

  const [from, setFrom] = useState(ukDate(29));
  const [to, setTo] = useState(ukDate());
  const [source, setSource] = useState('');
  const [campaign, setCampaign] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [editing, setEditing] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/acquisition/report', { params: { from, to } });
      setReport(data);
    } catch (err) {
      toast(err.response?.data?.error || 'Could not load the acquisition report.', { tone: 'error' });
    } finally {
      setLoading(false);
    }
  }, [from, to, toast]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/acquisition/campaigns');
      setCampaigns(data);
    } catch {
      toast('Could not load campaigns.', { tone: 'error' });
    }
  }, [toast]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const allRows = useMemo(() => report?.rows || [], [report]);
  const sources = useMemo(() => [...new Set(allRows.map((r) => r.source))].sort(), [allRows]);
  const campaignOptions = useMemo(
    () => [...new Set(allRows.filter((r) => !source || r.source === source).map((r) => r.campaign))].sort(),
    [allRows, source]
  );
  const rows = useMemo(
    () => allRows.filter((r) => (!source || r.source === source) && (!campaign || r.campaign === campaign)),
    [allRows, source, campaign]
  );
  const totals = useMemo(() => sumRows(rows), [rows]);

  const downloadCsv = () => {
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `acquisition_${from}_to_${to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (c) => {
    const ok = await confirm({
      title: 'Delete campaign',
      message: `Delete "${c.name}"?`,
      detail: 'Only the campaign and its spend entry are removed. Cases and tracking events are kept.',
      confirmLabel: 'Delete',
      tone: 'danger'
    });
    if (!ok) return;
    try {
      await api.delete(`/api/admin/acquisition/campaigns/${c._id}`);
      setCampaigns((prev) => prev.filter((x) => x._id !== c._id));
    } catch {
      toast('Could not delete the campaign.', { tone: 'error' });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Paid campaigns"
          description="Spend is entered by hand from the ad platform. Counts use the campaign's own dates, so spend and results always cover the same period."
          actions={
            <Button size="sm" onClick={() => setEditing({})}>
              <IconPlus size={15} />
              New campaign
            </Button>
          }
        />
        {campaigns.length === 0 && (
          <EmptyState
            title="No campaigns yet"
            description="Add the Reddit test campaign with its utm_campaign, dates and $150 budget to see cost per report."
          />
        )}
      </Card>

      {campaigns.map((c) => (
        <CampaignCard key={c._id} campaign={c} onEdit={setEditing} onDelete={handleDelete} />
      ))}

      <Card>
        <CardHeader
          title="All sources"
          description="Funnel by latest-touch utm_source and utm_campaign, UK calendar days. Visits without UTM tags count as direct."
        />
        <FilterBar
          actions={
            <>
              <Button variant="secondary" size="sm" onClick={downloadCsv} disabled={!rows.length}>
                <IconDownload size={15} />
                CSV
              </Button>
              <Button variant="secondary" size="sm" onClick={() => { fetchReport(); fetchCampaigns(); }} loading={loading}>
                <IconRefresh size={15} />
                Refresh
              </Button>
            </>
          }
        >
          <Input type="date" aria-label="From" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="h-9 w-auto text-[13px]" />
          <span className="text-sm text-ink-500">to</span>
          <Input type="date" aria-label="To" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="h-9 w-auto text-[13px]" />
          <FilterSelect label="Source" value={source} onChange={(e) => { setSource(e.target.value); setCampaign(''); }}>
            <option value="">All</option>
            {sources.map((s) => <option key={s} value={s}>{s}</option>)}
          </FilterSelect>
          <FilterSelect label="Campaign" value={campaign} onChange={(e) => setCampaign(e.target.value)}>
            <option value="">All</option>
            {campaignOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </FilterSelect>
        </FilterBar>

        {loading && !report ? (
          <TableSkeleton rows={4} cols={8} />
        ) : rows.length === 0 ? (
          <EmptyState title="Nothing in this range" description="No visits, report starts or customer reports for these filters." />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Source</Th>
                <Th>Campaign</Th>
                <Th className="text-right">Visits</Th>
                <Th className="text-right">Report starts</Th>
                <Th className="text-right">Completed reports</Th>
                <Th className="text-right">Valid</Th>
                <Th className="text-right">Dup. / invalid</Th>
                <Th className="text-right">Start → submit</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Tr key={`${r.source}|${r.campaign}`}>
                  <Td className="font-600 text-ink-900">{r.source}</Td>
                  <Td>{r.campaign}</Td>
                  <Td className="text-right tabular-nums">{formatNumber(r.visits)}</Td>
                  <Td className="text-right tabular-nums">{formatNumber(r.starts)}</Td>
                  <Td className="text-right tabular-nums">{formatNumber(r.submissions)}</Td>
                  <Td className="text-right tabular-nums">{formatNumber(r.valid)}</Td>
                  <Td className="text-right tabular-nums">{r.duplicate} / {r.invalid}</Td>
                  <Td className="text-right tabular-nums">{percent(r.startToSubmitRate)}</Td>
                </Tr>
              ))}
              <tr className="bg-ink-50 font-600 text-ink-900">
                <Td colSpan={2}>Total</Td>
                <Td className="text-right tabular-nums">{formatNumber(totals.visits)}</Td>
                <Td className="text-right tabular-nums">{formatNumber(totals.starts)}</Td>
                <Td className="text-right tabular-nums">{formatNumber(totals.submissions)}</Td>
                <Td className="text-right tabular-nums">{formatNumber(totals.valid)}</Td>
                <Td className="text-right tabular-nums">{totals.duplicate} / {totals.invalid}</Td>
                <Td className="text-right tabular-nums">{percent(totals.startToSubmitRate)}</Td>
              </tr>
            </tbody>
          </TableWrap>
        )}
      </Card>

      {editing && (
        <CampaignModal
          campaign={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            setCampaigns((prev) =>
              prev.some((c) => c._id === saved._id)
                ? prev.map((c) => (c._id === saved._id ? saved : c))
                : [saved, ...prev]
            );
            toast('Campaign saved.', { tone: 'success' });
          }}
        />
      )}
    </div>
  );
};

export default AcquisitionTracking;
